// src/components/AgentHub.tsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Chip,
  IconButton,
  Divider,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  SelectChangeEvent,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import PublicIcon from "@mui/icons-material/Public";
import BusinessIcon from "@mui/icons-material/Business"; 
import FaceIcon from "@mui/icons-material/Face"; 
import LightbulbIcon from "@mui/icons-material/Lightbulb"; // ✅ 아이콘 추가
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../services/api";

// --- Types ---
interface AgentStep {
  step_name: string;
  step_prompt?: string; // ✅ [추가] 프롬프트
  mcp_module_keys: string[];
  data_bundle_ids?: string[];
}

interface AgentItem {
  id: number;
  name: string;
  description: string;
  steps: AgentStep[];
  org_id: string; 
  user_id: string; 
  user_tier?: number; 
  created_at: string;
}

interface MyAccessItem {
  module_key: string;
  display_name?: string;
}

interface DataBundleItem {
  id: string;
  name: string;
  description?: string;
}

interface Props {
  setErrorMsg: (msg: string | null) => void;
}

export default function AgentHub({ setErrorMsg }: Props) {
  const navigate = useNavigate();

  // --- States ---
  const [agents, setAgents] = useState<AgentItem[]>([]);
  const [myMcps, setMyMcps] = useState<MyAccessItem[]>([]);
  const [myBundles, setMyBundles] = useState<DataBundleItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Dialog States
  const [openDialog, setOpenDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newSteps, setNewSteps] = useState<AgentStep[]>([]);
  const [creating, setCreating] = useState(false);

  // Current Step Input
  const [stepNameInput, setStepNameInput] = useState("");
  const [stepPromptInput, setStepPromptInput] = useState(""); // ✅ [추가]
  const [selectedMcps, setSelectedMcps] = useState<string[]>([]);
  const [selectedBundles, setSelectedBundles] = useState<string[]>([]);

  // 내 아이디
  const myUserId = localStorage.getItem("user_id") || "";

  const authHeaders = useMemo(
    () => ({
      "x-user-id": localStorage.getItem("user_id") || "",
      "x-access-token": localStorage.getItem("access_token") || "",
      "x-refresh-token": localStorage.getItem("refresh_token") || "",
    }),
    []
  );

  // --- Fetch Data ---
  const fetchMyAgents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}agents`, { headers: authHeaders });
      if (res.data?.success) {
        setAgents(res.data.data || []);
      } else {
        setAgents([]);
      }
    } catch (e) {
      console.error(e);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      // 1. MCP 목록 조회
      const mcpRes = await axios.get(`${API_BASE}mcp/me/access`, {
        headers: authHeaders,
        params: { status: "approved" },
      });
      if (mcpRes.data.success) {
        setMyMcps(mcpRes.data.data || []);
      }

      // 2. Data Bundles (DataLake 연동)
      const bundleRes = await axios.get(`${API_BASE}datalake/bundles`, {
        headers: authHeaders,
      });
      
      if (bundleRes.data && (Array.isArray(bundleRes.data) || bundleRes.data.success)) {
          const list = Array.isArray(bundleRes.data) ? bundleRes.data : (bundleRes.data.data || []);
          
          // 🚨 [수정된 부분] b.id 대신 b.bundle_id를 사용해야 합니다!
          setMyBundles(list.map((b: any) => ({
              id: b.bundle_id,  // ✅ 여기가 핵심! (console.log 참고)
              name: b.name,
              description: b.description
          })));
      } else {
          setMyBundles([]);
      }

    } catch (e) {
      console.warn("리소스 목록 로드 실패", e);
      setMyBundles([]); 
    }
  };

  useEffect(() => {
    fetchMyAgents();
    fetchResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 그룹핑 로직
  const groupedAgents = useMemo(() => {
    const global: AgentItem[] = [];       
    const orgOfficial: AgentItem[] = [];  
    const teams: Record<string, AgentItem[]> = {}; 

    agents.forEach((agent) => {
      if (agent.org_id === "#GLOBAL#") {
        global.push(agent);
      } 
      else if (agent.user_tier === 0) {
        orgOfficial.push(agent);
      } 
      else {
        if (!teams[agent.user_id]) {
          teams[agent.user_id] = [];
        }
        teams[agent.user_id].push(agent);
      }
    });

    return { global, orgOfficial, teams };
  }, [agents]);


  // --- Handlers ---
  const handleOpen = () => {
    setNewName("");
    setNewDesc("");
    setNewSteps([]);
    setStepNameInput("");
    setStepPromptInput(""); // ✅ 초기화
    setSelectedMcps([]);
    setSelectedBundles([]);
    setOpenDialog(true);
  };

  const handleStepMcpChange = (event: SelectChangeEvent<typeof selectedMcps>) => {
    const { value } = event.target;
    setSelectedMcps(typeof value === "string" ? value.split(",") : value);
  };

  const handleStepBundleChange = (event: SelectChangeEvent<typeof selectedBundles>) => {
    const { value } = event.target;
    setSelectedBundles(typeof value === "string" ? value.split(",") : value);
  };

  const handleAddStep = () => {
    if (!stepNameInput.trim()) return;
    setNewSteps((prev) => [
      ...prev,
      {
        step_name: stepNameInput,
        step_prompt: stepPromptInput, // ✅ [추가]
        mcp_module_keys: selectedMcps,
        data_bundle_ids: selectedBundles,
      },
    ]);
    setStepNameInput("");
    setStepPromptInput(""); // ✅ 초기화
    setSelectedMcps([]);
    setSelectedBundles([]);
  };

  const handleRemoveStep = (idx: number) => {
    setNewSteps((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCreateAgent = async () => {
    if (!newName.trim() || newSteps.length === 0) return;

    setCreating(true);
    try {
      const payload = {
        name: newName,
        description: newDesc,
        steps: newSteps,
      };

      const res = await axios.post(`${API_BASE}agents`, payload, { headers: authHeaders });
      if (res.data?.success) {
        fetchMyAgents();
        setOpenDialog(false);
        setErrorMsg(null);
      } else {
        setErrorMsg(res.data?.message || "Agent 생성 실패");
      }
    } catch (e: any) {
      console.error(e);
      setErrorMsg("Agent 생성 중 오류가 발생했습니다.");
    } finally {
      setCreating(false);
    }
  };

  const goToDetail = (agentId: number) => {
    navigate(`/agent/${agentId}`);
  };

  // 카드 렌더링 컴포넌트
  const AgentCard = ({ agent, type }: { agent: AgentItem, type: 'global' | 'org' | 'team' }) => {
    let borderStyle = "1px solid #333";
    let iconColor: "secondary" | "primary" | "inherit" = "inherit";
    let badge = null;

    if (type === 'global') {
      borderStyle = "4px solid #9c27b0"; 
      iconColor = "secondary";
      badge = <Chip icon={<PublicIcon style={{ fontSize: 16 }} />} label="전체 공용" size="small" color="secondary" sx={{ fontWeight: "bold" }} />;
    } else if (type === 'org') {
      borderStyle = "4px solid #1976d2"; 
      iconColor = "primary";
      badge = <Chip icon={<BusinessIcon style={{ fontSize: 16 }} />} label="조직 공식" size="small" color="primary" variant="outlined" sx={{ fontWeight: "bold", borderWidth: 1 }} />;
    } else {
      borderStyle = "1px solid #555"; 
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          bgcolor: "#1e1e1e",
          cursor: "pointer",
          transition: "all 0.2s",
          borderLeft: borderStyle,
          "&:hover": {
            boxShadow: "0 0 8px rgba(255, 255, 255, 0.1)",
            bgcolor: "#252525",
          },
        }}
        onClick={() => goToDetail(agent.id)}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <SmartToyIcon color={iconColor} />
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: "bold" }}>
              {agent.name}
            </Typography>
          </Box>
          {badge}
        </Box>

        <Typography variant="body2" color="grey.400" mb={3} sx={{ ml: 4 }}>
          {agent.description}
        </Typography>

        {/* Workflow Visualization */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            overflowX: "auto",
            p: 2,
            bgcolor: "#121212",
            borderRadius: 2,
            border: "1px solid #333",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Chip label="Start" color="default" variant="outlined" sx={{ color: "#fff", borderColor: "#555" }} />
          <ArrowForwardIcon sx={{ color: "#555", fontSize: 16 }} />

          {agent.steps.map((step, idx) => (
            <React.Fragment key={idx}>
              <Box
                sx={{
                  border: "1px solid #333",
                  borderRadius: 1,
                  p: 1.5,
                  minWidth: 160,
                  textAlign: "left",
                  bgcolor: "#1e1e1e",
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" color="primary.light" gutterBottom>
                  {idx + 1}. {step.step_name}
                </Typography>
                
                {/* ✅ [추가] 카드 뷰에서도 프롬프트 아이콘 표시 (툴팁 등으로) */}
                {step.step_prompt && (
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        <LightbulbIcon sx={{ fontSize: 12, color: "#fbc02d" }} />
                        <Typography variant="caption" color="grey.500" sx={{ fontSize: "0.65rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>
                            {step.step_prompt}
                        </Typography>
                    </Box>
                )}

                {step.mcp_module_keys.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={0.5}>
                    {step.mcp_module_keys.map((k) => (
                      <Chip key={k} label={k} size="small" icon={<SettingsSuggestIcon style={{ fontSize: 12 }} />} sx={{ fontSize: "0.7rem", height: 20 }} />
                    ))}
                  </Stack>
                )}
                {step.data_bundle_ids && step.data_bundle_ids.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {step.data_bundle_ids.map((bundleId) => {
                      // myBundles에서 ID로 이름을 찾음 (없으면 ID 그대로 표시)
                      const bundleName = myBundles.find(b => b.id === bundleId)?.name || bundleId;
                      return (
                        <Chip 
                          key={bundleId} 
                          label={bundleName} // <-- 여기가 핵심!
                          size="small" 
                          color="secondary" 
                          variant="outlined" 
                          icon={<StorageIcon style={{ fontSize: 12 }} />} 
                          sx={{ fontSize: "0.7rem", height: 20 }} 
                        />
                      );
                    })}
                  </Stack>
                )}
              </Box>
              <ArrowForwardIcon sx={{ color: "#555", fontSize: 16 }} />
            </React.Fragment>
          ))}
          <Chip label="End" color="default" variant="outlined" sx={{ color: "#fff", borderColor: "#555" }} />
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      {/* 상단 로딩 표시 */}
      {loading && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>Agent 목록을 불러오는 중...</Typography>
          </Box>
          <LinearProgress />
        </Paper>
      )}

      {/* 헤더 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Agent Hub</Typography>
          <Typography variant="body2" color="text.secondary">
            구독(승인)한 MCP와 Data Bundle을 연결하여 나만의 업무 자동화 Agent를 생성하세요.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
          New Agent
        </Button>
      </Box>

      {/* 결과 없음 */}
      {!loading && agents.length === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 6, textAlign: "center", bgcolor: "#1e1e1e", color: "#888" }}>
              <SmartToyIcon sx={{ fontSize: 48, mb: 2, color: "#555" }} />
              <Typography variant="h6" gutterBottom>등록된 Agent가 없습니다.</Typography>
              <Typography variant="body2">우측 상단 'New Agent' 버튼을 눌러 새로운 Agent를 만들어보세요.</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* 섹션 1: 전체 공용 (#GLOBAL#) */}
      {groupedAgents.global.length > 0 && (
        <Box mb={5}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <PublicIcon color="secondary" />
            <Typography variant="h6" fontWeight="bold" color="#ce93d8">
              전체 공용 (Global Agents)
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {groupedAgents.global.map((agent) => (
              <Grid item xs={12} key={agent.id}>
                <AgentCard agent={agent} type="global" />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* 섹션 2: 조직 공용 (Tier 0 생성) */}
      {groupedAgents.orgOfficial.length > 0 && (
        <Box mb={5}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <BusinessIcon color="primary" />
            <Typography variant="h6" fontWeight="bold" color="#90caf9">
              {groupedAgents.orgOfficial[0].org_id} 조직 공용 (Official)
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {groupedAgents.orgOfficial.map((agent) => (
              <Grid item xs={12} key={agent.id}>
                <AgentCard agent={agent} type="org" />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* 섹션 3: 팀/개인 Agent (User ID별로 그룹핑) */}
      {Object.entries(groupedAgents.teams).map(([userId, teamAgents]) => (
        <Box mb={5} key={userId}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <FaceIcon sx={{ color: "#aaa" }} />
            <Typography variant="h6" fontWeight="bold" color="#eee">
              {userId} 팀 Agent
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {teamAgents.map((agent) => (
              <Grid item xs={12} key={agent.id}>
                <AgentCard agent={agent} type="team" />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Create Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: "#1e1e1e", color: "#fff" },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #333" }}>새 Agent 생성</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* 기본 정보 */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <TextField
                label="Agent 이름"
                fullWidth
                variant="outlined"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="예: 출장 정산 처리 Agent"
                InputLabelProps={{ style: { color: "#aaa" } }}
                InputProps={{
                  style: { color: "#fff" },
                  sx: { ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" } },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="설명"
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                InputLabelProps={{ style: { color: "#aaa" } }}
                InputProps={{
                  style: { color: "#fff" },
                  sx: { ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" } },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2, borderColor: "#333" }}>
                <Typography variant="caption" sx={{ color: "#888" }}>
                  Workflow 구성 (승인된 MCP 및 Data Bundle 사용)
                </Typography>
              </Divider>
            </Grid>

            {/* Step Adder Form */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "rgba(255,255,255,0.05)", borderColor: "#444" }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: "#fff" }}>
                  단계 추가
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="단계 이름"
                      fullWidth
                      size="small"
                      value={stepNameInput}
                      onChange={(e) => setStepNameInput(e.target.value)}
                      placeholder="예: 데이터 수집"
                      InputLabelProps={{ style: { color: "#aaa" } }}
                      InputProps={{
                        style: { color: "#fff" },
                        sx: { ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" } },
                      }}
                    />
                  </Grid>
                  {/* ✅ [추가] 프롬프트 입력 */}
                  <Grid item xs={12} sm={9}>
                    <TextField
                      label="프롬프트 (선택)"
                      fullWidth
                      size="small"
                      value={stepPromptInput}
                      onChange={(e) => setStepPromptInput(e.target.value)}
                      placeholder="이 단계의 구체적 지시사항"
                      InputLabelProps={{ style: { color: "#aaa" } }}
                      InputProps={{
                        style: { color: "#fff" },
                        sx: { ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" } },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ color: "#aaa" }}>MCP 선택 (다중)</InputLabel>
                      <Select
                        multiple
                        value={selectedMcps}
                        onChange={handleStepMcpChange}
                        input={
                          <OutlinedInput
                            label="MCP 선택 (다중)"
                            sx={{
                              color: "#fff",
                              ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                            }}
                          />
                        }
                        MenuProps={{ PaperProps: { sx: { bgcolor: "#333", color: "#fff" } } }}
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" sx={{ bgcolor: "#555", color: "#fff" }} />
                            ))}
                          </Box>
                        )}
                      >
                        {myMcps.length === 0 && <MenuItem disabled>승인된 MCP 없음</MenuItem>}
                        {myMcps.map((m) => (
                          <MenuItem key={m.module_key} value={m.module_key}>
                            {m.module_key}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ color: "#aaa" }}>Data Bundle (다중)</InputLabel>
                    <Select
                      multiple
                      value={selectedBundles}
                      onChange={handleStepBundleChange}
                      input={
                        <OutlinedInput
                          label="Data Bundle (다중)"
                          sx={{
                            color: "#fff",
                            ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                          }}
                        />
                      }
                      MenuProps={{ PaperProps: { sx: { bgcolor: "#333", color: "#fff" } } }}
                      
                      // ✅ [수정된 부분] renderValue 로직 변경
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => {
                            // 1. 선택된 ID(value)에 해당하는 번들 객체를 찾음
                            const bundle = myBundles.find((b) => b.id === value);
                            // 2. 찾았으면 그 이름(name)을, 못 찾았으면 ID 자체를 표시
                            return (
                              <Chip 
                                key={value} 
                                label={bundle ? bundle.name : value} 
                                size="small" 
                                color="secondary" 
                                variant="outlined" 
                                sx={{ color: "#ff8a80", borderColor: "#ff8a80" }} 
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {myBundles.length === 0 && <MenuItem disabled>사용 가능한 번들 없음</MenuItem>}
                      {myBundles.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddStep}
                      disabled={!stepNameInput || (selectedMcps.length === 0 && selectedBundles.length === 0)}
                      sx={{ height: 40 }}
                    >
                      추가
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Steps List Preview */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "rgba(0,0,0,0.3)",
                  minHeight: 100,
                  borderColor: "#444",
                }}
              >
                {newSteps.length === 0 ? (
                  <Typography variant="body2" color="grey.500" align="center" sx={{ mt: 3 }}>
                    추가된 단계가 없습니다. 위에서 단계를 추가해주세요.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {newSteps.map((s, idx) => (
                      <Box
                        key={idx}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          bgcolor: "#2c2c2c",
                          p: 1.5,
                          borderRadius: 1,
                          border: "1px solid #444",
                        }}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                            <Chip label={`Step ${idx + 1}`} size="small" color="primary" />
                            <Typography variant="body2" fontWeight="bold" sx={{ color: "#fff" }}>
                              {s.step_name}
                            </Typography>
                          </Stack>
                          {/* ✅ [추가] 미리보기에도 프롬프트 요약 */}
                          {s.step_prompt && (
                              <Typography variant="caption" sx={{ color: "#fbc02d", display: "block", mb: 0.5 }}>
                                  💡 {s.step_prompt.substring(0, 30)}...
                              </Typography>
                          )}
                          <Stack direction="row" spacing={1} alignItems="center">
                            {s.mcp_module_keys.length > 0 ? (
                              <Typography variant="caption" sx={{ color: "#ccc" }}>
                                MCP: {s.mcp_module_keys.join(", ")}
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: "#666" }}>
                                No MCP
                              </Typography>
                            )}

                            <Divider orientation="vertical" flexItem sx={{ bgcolor: "#555" }} />

                            {s.data_bundle_ids && s.data_bundle_ids.length > 0 ? (
                            <Typography variant="caption" sx={{ color: "#ff8a80" }}>
                              Data: {s.data_bundle_ids.map(bid => {
                                  // ID 매핑 로직 추가
                                  return myBundles.find(b => b.id === bid)?.name || bid;
                              }).join(", ")}
                            </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: "#666" }}>
                                No Data
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                        <IconButton size="small" onClick={() => handleRemoveStep(idx)} sx={{ color: "#f44336" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #333" }}>
          <Button onClick={() => setOpenDialog(false)} disabled={creating} sx={{ color: "#aaa" }}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateAgent}
            disabled={!newName || newSteps.length === 0 || creating}
          >
            {creating ? "생성 중..." : "생성"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}